import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { extractApiErrorMessage } from '@frontend/api/http';
import { useInstanceId } from '@frontend/hooks';
import { ToolService, ToolServiceCreationPayload } from '@frontend/types';

import {
  createToolRegistry,
  deleteToolRegistry,
  getToolRegistries,
  syncToolRegistry,
} from '../services';
import { getTools } from '../services/toolsService';

export function useGetToolRegistries(instanceId: string) {
  const {
    data: toolRegistries,
    isPending,
    error,
  } = useQuery<ToolService>({
    queryKey: getToolRegistries({ instanceId }).key,
    queryFn: getToolRegistries({ instanceId }).queryFn,
  });

  return { toolRegistries: toolRegistries ?? [], isPending: isPending, error };
}

export function useCreateToolRegistry(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { instanceId } = useInstanceId();
  const { mutate, isPending, error } = useMutation({
    mutationFn: (payload: ToolServiceCreationPayload) => createToolRegistry(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getToolRegistries({ instanceId: instanceId || '' }).key,
      });
      queryClient.invalidateQueries({
        queryKey: getTools({ instanceId: instanceId || '', include_disabled: true }).key,
      });
      toaster.create('Tool registry created successfully', { type: 'success' });
      onSuccess?.();
    },
    onError: error => {
      const errorMessage = extractApiErrorMessage(error);
      toaster.create(errorMessage, { type: 'danger' });
    },
  });
  return { mutate, isPending, error };
}
export function useSyncToolRegistry() {
  const queryClient = useQueryClient();
  const { instanceId } = useInstanceId();
  const { mutate, isPending, error } = useMutation({
    mutationFn: (registryId: string) => syncToolRegistry(registryId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getToolRegistries({ instanceId: instanceId || '' }).key,
      });
      queryClient.invalidateQueries({
        queryKey: getTools({ instanceId: instanceId || '', include_disabled: true }).key,
      });
      toaster.create('Tool registry synced successfully!', { type: 'success' });
    },
  });
  return { mutate, isPending, error };
}

export function useDeleteToolRegistry(instanceId: string) {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, string>({
    mutationFn: registryId => deleteToolRegistry(registryId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getToolRegistries({ instanceId }).key,
      });
      queryClient.invalidateQueries({
        queryKey: getTools({ instanceId, include_disabled: true }).key,
      });
      toaster.create('Tool registry deleted successfully!', { type: 'success' });
    },
    onError: () => {
      toaster.create('Failed to delete tool registry', { type: 'danger' });
    },
  });
}
